import BlockIcon from "@mui/icons-material/Block";
import CheckIcon from "@mui/icons-material/Check";
import { Box, IconButton, MenuItem, TextField, Tooltip } from "@mui/material";
import { type FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { SystemValidationTextField } from "../../../../../components/UIToolkit/Inputs/SystemValidationTextField";
import SystemDialog from "../../../../../components/UIToolkit/Popups/Dialog";
import SystemButton from "../../../../../components/UIToolkit/SystemButton/SystemButton";

// ───── Enum ─────

export enum HrisMappingView {
	Roles = "roles",
	Employment = "employment",
	Status = "status",
	TimeOff = "timeOff",
	PunchEventType = "punchEventType",
	EarningCode = "earningCode",
	SubRole = "subRole",
}

/**
 * Mirror of the backend's `_normalize_role_string` (backend/database/jobs.py).
 * Used everywhere the FE joins on role_string between cert mappings and raw HRIS employees.
 *
 * Removes dots, replaces dashes/slashes with spaces, removes parentheses, collapses
 * whitespace, strips leading numeric codes (e.g. "130 RN" -> "rn"), and strips trailing
 * numeric codes (e.g. "RN 0011" -> "rn").
 *
 * Backend stores the override row with this normalization applied, so the raw HRIS role
 * (e.g. "CNA-Trainee") and the persisted mapping role_string (e.g. "cna trainee") only
 * match when both sides go through this transform first.
 */
export function normalizeRoleString(raw: string): string {
	const cleaned = raw.replace(/\./g, "").replace(/-/g, " ").replace(/\//g, " ").replace(/[()]/g, "");
	const collapsed = cleaned.replace(/\s+/g, " ").trim();
	const leadingStripped = collapsed.replace(/^\d+\s+/, "").trim();
	const stripped = leadingStripped.replace(/\s*\d+$/, "").trim();
	// If stripping trailing numbers leaves nothing (e.g. "999"), keep the leading-stripped string.
	return (stripped || leadingStripped).toLowerCase();
}

// ───── Hover-action cell helpers ─────

/** Renders a green ✓ that swaps to an action icon on hover (CSS-only, no state). */
export const HoverActionCell: FC<{
	show: boolean;
	actionIcon: React.ReactNode;
	onAction: () => void;
	tooltip?: string;
}> = ({ show, actionIcon, onAction, tooltip }) => {
	if (!show) return null;
	const content = (
		<Box
			sx={{
				display: "flex",
				alignItems: "center",
				width: "100%",
				justifyContent: "center",
				"& .hover-action-btn": { display: "none" },
				"& .hover-check-icon": { display: "flex" },
				"&:hover .hover-action-btn": { display: "flex" },
				"&:hover .hover-check-icon": { display: "none" },
			}}
		>
			<CheckIcon className="hover-check-icon" fontSize="small" sx={{ color: "success.main" }} />
			<IconButton className="hover-action-btn" size="small" onClick={onAction}>
				{actionIcon}
			</IconButton>
		</Box>
	);
	return tooltip ? <Tooltip title={tooltip}>{content}</Tooltip> : content;
};

// ───── Ignored-column cell ─────

/** State of a row in the HRIS ignore list, used by the "Ignored" grid column. */
export type IgnoredCellState = { id: number; isGlobal: boolean } | undefined;

/** Confirmation dialog used before removing a value from the ignore list. Reused by the
 * mapping grids (per-row unignore) and the standalone ignore-list grid (delete action). */
export const ConfirmRemoveIgnoreDialog: FC<{ open: boolean; rawValue: string; onClose: () => void; onConfirm: () => void }> = ({
	open,
	rawValue,
	onClose,
	onConfirm,
}) => {
	const { t } = useTranslation();
	return (
		<SystemDialog
			open={open}
			onClose={onClose}
			title={t("hris.mapping.ignored.confirmRemove.title")}
			body={t("hris.mapping.ignored.confirmRemove.body", { value: rawValue })}
			onConfirm={() => {
				onConfirm();
				onClose();
			}}
		/>
	);
};

/**
 * Visual indicator + toggle for the "Ignored" column on the mapping grids.
 *
 * The default (non-hover) state differs by ignore status so admins can tell at a glance
 * which rows are blocked from sync — a previous version showed the same green ✓ for both
 * states, which was indistinguishable without hovering.
 *
 * - Ignored (per-dept): solid block icon, click prompts confirmation before removing.
 * - Ignored globally: solid disabled block icon, can't be removed from the dept-scoped grid.
 * - Not ignored: faint block icon, click to add (no confirmation — adding is reversible).
 */
export const IgnoredCell: FC<{ ignore: IgnoredCellState; rawValue: string; onToggle: () => void }> = ({ ignore, rawValue, onToggle }) => {
	const { t } = useTranslation();
	const [confirmOpen, setConfirmOpen] = useState(false);
	if (ignore) {
		const isGlobal = ignore.isGlobal;
		const tooltip = isGlobal ? t("hris.mapping.ignored.global") : t("hris.mapping.ignored.unignore");
		return (
			<>
				<Tooltip title={tooltip}>
					<span>
						<IconButton size="small" disabled={isGlobal} onClick={isGlobal ? undefined : () => setConfirmOpen(true)}>
							<BlockIcon fontSize="small" color={isGlobal ? "disabled" : "warning"} />
						</IconButton>
					</span>
				</Tooltip>
				<ConfirmRemoveIgnoreDialog open={confirmOpen} rawValue={rawValue} onClose={() => setConfirmOpen(false)} onConfirm={onToggle} />
			</>
		);
	}
	return (
		<Tooltip title={t("hris.mapping.ignored.add")}>
			<IconButton size="small" onClick={onToggle} sx={{ opacity: 0.25, "&:hover": { opacity: 1 } }}>
				<BlockIcon fontSize="small" color="action" />
			</IconButton>
		</Tooltip>
	);
};

// ───── Add Custom Mapping button + dialog ─────

/** Describes one extra singleSelect field to collect in the Add Custom dialog. */
export type AddCustomFieldSpec = {
	/** Payload key in the `extras` object returned to onAdd. */
	name: string;
	label: string;
	options: { value: string; label: string }[];
};

interface AddCustomMappingButtonProps {
	keyLabel: string;
	/** Extra fields to render as dropdowns after the key input. All are required. */
	extraFields?: AddCustomFieldSpec[];
	onAdd: (key: string, extras: Record<string, string>) => void;
	/** Controlled open state, letting a second trigger (e.g. an EmptyState action) drive the same dialog. Uncontrolled when omitted. */
	open?: boolean;
	onOpenChange?: (open: boolean) => void;
	/** Hide the built-in trigger button (dialog still renders, driven by `open`). */
	hideTriggerButton?: boolean;
	/** Override when more than one trigger button renders on a page. */
	dataTestId?: string;
}

export const AddCustomMappingButton: FC<AddCustomMappingButtonProps> = ({
	keyLabel,
	extraFields = [],
	onAdd,
	open,
	onOpenChange,
	hideTriggerButton,
	dataTestId = "hris-mapping-add-custom",
}) => {
	const { t } = useTranslation();
	const [internalOpen, setInternalOpen] = useState(false);
	const isControlled = open !== undefined;
	const dialogOpen = isControlled ? open : internalOpen;
	const setDialogOpen = (next: boolean) => {
		if (!isControlled) setInternalOpen(next);
		onOpenChange?.(next);
	};
	const [value, setValue] = useState("");
	const [extras, setExtras] = useState<Record<string, string>>({});

	const close = () => {
		setDialogOpen(false);
		setValue("");
		setExtras({});
	};

	const handleAdd = () => {
		const trimmed = value.trim();
		if (!trimmed) return;
		if (extraFields.some((f) => !extras[f.name])) return;
		onAdd(trimmed, extras);
		close();
	};

	const canSubmit = value.trim() !== "" && extraFields.every((f) => !!extras[f.name]);

	return (
		<Box
			sx={{
				display: "flex",
				gap: 1,
				alignItems: "center",
			}}
		>
			{!hideTriggerButton && (
				<SystemButton buttonVariant={"secondary"} onClick={() => setDialogOpen(true)} sx={{ alignSelf: "flex-start" }} data-testid={dataTestId}>
					{t("hris.mapping.addCustom")}
				</SystemButton>
			)}
			<SystemDialog
				open={dialogOpen}
				onClose={close}
				title={t("hris.mapping.addCustom.dialog.title")}
				body={
					<Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
						<SystemValidationTextField
							autoFocus
							fullWidth
							label={keyLabel}
							value={value}
							onChange={(e) => setValue(e.target.value)}
							onKeyDown={(e) => {
								if (e.key === "Enter" && canSubmit) {
									e.stopPropagation();
									e.preventDefault();
									handleAdd();
								}
							}}
						/>
						{extraFields.map((field) => (
							<TextField
								key={field.name}
								select
								fullWidth
								label={field.label}
								value={extras[field.name] ?? ""}
								onChange={(e) => setExtras((prev) => ({ ...prev, [field.name]: e.target.value }))}
							>
								{field.options.map((opt) => (
									<MenuItem key={opt.value} value={opt.value}>
										{opt.label}
									</MenuItem>
								))}
							</TextField>
						))}
					</Box>
				}
				confirmDisabled={!canSubmit}
				onConfirm={handleAdd}
			/>
		</Box>
	);
};
